/**
 * contract-view.js — hydrate the signed lay-by agreement from its snapshot (NZ).
 *
 * Reads ?t=<token>, fetches the frozen document, and writes it into the markup.
 * Port of the US/UK asset with three AU differences:
 *   - The plan block is the AU/NZ shape ({paymentFrequency, installmentCents,
 *     totalPayments, …}) rather than the US/UK computed one ({intervalDays,
 *     weeks, depositCents, …}). Both are handled, snapshot-first.
 *   - Totals name GST (gstCents) rather than sales tax/VAT (taxCents).
 *   - The API may return `evidence` — the vaulted instrument (with its Stripe
 *     card fingerprint), the consent record, the bank account we debit and the
 *     live payment history. Rendered below the signature; printed, that is a
 *     dispute response.
 *   - The debit account is snapshot-first: `contract.debitAccount` is the
 *     account as frozen at signing and always wins, with `evidence.debitAccount`
 *     (read live) covering agreements signed before the snapshot carried it.
 *
 * RULES THIS FILE FOLLOWS, because the whole point is that the page cannot lie:
 *   - It NEVER computes a payment, a total or a date. Every number rendered is a
 *     number the API already supplied. If a figure is missing it shows an
 *     em dash, not a guess.
 *   - Money comes from the snapshot in CENTS and is formatted with the currency
 *     the snapshot names, not the shop's current currency.
 *   - Terms are inserted as TEXT, never as HTML. They are a legal artefact from
 *     the database; treating them as markup would make a stored value executable.
 *   - No retry-on-404. A missing token is an answer, not a transient failure.
 *
 * Written as ES5-compatible script (no modules, no optional chaining) to match
 * the rest of this theme's assets, which are served unbundled.
 */
(function () {
  'use strict';

  var root = document.querySelector('.contract-view');
  if (!root) return;

  var params = new URLSearchParams(window.location.search);

  // Two contract pages share /pages/contract: this one owns ?t= (snapshot),
  // the legacy dynamic-contract-template owns ?ref= (live order, pre-snapshot
  // links). With no ?t we stay hidden and let the legacy section render —
  // unless there is no ?ref either, in which case we surface the honest
  // "can't find it" state.
  if (!params.get('t') && params.get('ref')) return;
  root.hidden = false;
  // Unlocks the print rules that strip everything except this document. They
  // must NOT apply when the legacy ?ref= page is the one being printed — it is
  // a sibling section on this same template, and printing it blanked the page.
  document.documentElement.classList.add('contract-view-active');

  var endpoint = (root.getAttribute('data-endpoint') || '').replace(/\/$/, '');

  function show(state) {
    var all = root.querySelectorAll('[data-state]');
    for (var i = 0; i < all.length; i++) {
      all[i].hidden = all[i].getAttribute('data-state') !== state;
    }
  }

  function set(field, value) {
    var nodes = root.querySelectorAll('[data-f="' + field + '"]');
    for (var i = 0; i < nodes.length; i++) {
      nodes[i].textContent = value === null || value === undefined || value === '' ? '—' : value;
    }
  }

  /**
   * Payment cadence in words. AU/NZ snapshots carry the frequency the customer
   * chose at checkout as a string; US/UK snapshots carry intervalDays. Prefer
   * the words the customer picked, fall back to the interval, and return null
   * (an em dash) rather than asserting a cadence the agreement does not state.
   */
  function cadenceLabel(plan) {
    var freq = plan.paymentFrequency;
    if (typeof freq === 'string' && freq) {
      var f = freq.toLowerCase();
      if (f.indexOf('week') === 0) return 'Every week';
      if (f.indexOf('fort') === 0) return 'Every 2 weeks';
      if (f.indexOf('month') === 0) return 'Every month';
      return freq;
    }
    if (plan.intervalDays === 7) return 'Every week';
    if (plan.intervalDays === 14) return 'Every 2 weeks';
    if (typeof plan.intervalDays === 'number' && plan.intervalDays > 0) return 'Every ' + plan.intervalDays + ' days';
    return null;
  }

  /** Cents -> localised currency. Falls back to a plain figure if Intl is unhappy. */
  function money(cents, currency) {
    if (typeof cents !== 'number' || isNaN(cents)) return null;
    var code = (currency || 'nzd').toUpperCase();
    try {
      return new Intl.NumberFormat('en-NZ', { style: 'currency', currency: code }).format(cents / 100);
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  /** ISO date (YYYY-MM-DD) -> "Tue 12 Aug 2026", parsed as UTC so it cannot shift a day. */
  function dateLong(iso) {
    if (!iso) return null;
    var d = new Date(/^\d{4}-\d{2}-\d{2}$/.test(iso) ? iso + 'T00:00:00Z' : iso);
    if (isNaN(d.getTime())) return null;
    try {
      return new Intl.DateTimeFormat('en-NZ', {
        weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC'
      }).format(d);
    } catch (e) {
      return iso;
    }
  }

  function dateTimeLong(iso, tz) {
    if (!iso) return null;
    var d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    try {
      return new Intl.DateTimeFormat('en-NZ', {
        day: 'numeric', month: 'long', year: 'numeric',
        hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
        timeZone: tz || 'Pacific/Auckland'
      }).format(d);
    } catch (e) {
      return d.toISOString();
    }
  }

  function lines(el, arr) {
    if (!el) return;
    el.textContent = '';
    if (!arr || !arr.length) return;
    for (var i = 0; i < arr.length; i++) {
      if (i) el.appendChild(document.createElement('br'));
      el.appendChild(document.createTextNode(arr[i]));
    }
  }

  function renderEvidence(payload, cur) {
    var ev = payload.evidence || null;

    // Only the authorisation details render. The API also returns the live
    // payment ledger for NZ/AU, but a running payment record does not belong
    // on the signed document — the dispute pack reads it from the API instead.
    var authBlock = root.querySelector('[data-evidence-auth-block]');
    var dl = root.querySelector('[data-evidence-auth]');
    if (!authBlock || !dl) return;

    var inst = ev && ev.instrument;
    var consent = (ev && ev.consents && ev.consents.length) ? ev.consents[ev.consents.length - 1] : null;

    // The bank account this agreement authorises us to debit. The SNAPSHOT
    // wins: where it carries the account, that is the account as agreed and
    // frozen, and it must not be overwritten by a later change of bank. The
    // live record is the fallback for agreements signed before the snapshot
    // carried the field — those documents are immutable, so reading live is
    // the only way their page can name the account at all.
    var contract = payload.contract || {};
    var debit = contract.debitAccount || (ev && ev.debitAccount) || null;

    if (!inst && !consent && !debit) return;
    authBlock.hidden = false;
    dl.textContent = '';

    function row(label, value) {
      if (value === null || value === undefined || value === '') return;
      var div = document.createElement('div');
      var dt = document.createElement('dt');
      dt.textContent = label;
      var dd = document.createElement('dd');
      dd.textContent = value;
      div.appendChild(dt); div.appendChild(dd);
      dl.appendChild(div);
    }

    // The account first: on a direct debit agreement it is the operative term,
    // and every other row here describes how it was authorised.
    if (debit) {
      row('Account name', debit.accountName);
      row('Bank', debit.bankName);
      if (debit.bsb) {
        row('BSB', debit.bsb);
        row('Account number', debit.accountNumber);
      } else {
        // NZ numbers carry bank and branch inline, and an unparsed value has
        // no split to show — either way `display` is what we hold on file.
        row('Bank account', debit.display);
      }
    }

    if (inst) {
      var instLabel = [inst.brand, inst.last4 ? '•••• ' + inst.last4 : null].filter(Boolean).join(' ');
      row('Payment instrument', instLabel || inst.type);
      row('Expires', inst.expMonth && inst.expYear ? inst.expMonth + '/' + inst.expYear : null);
      // Stripe's stable identifier for the physical card — the same card always
      // yields the same fingerprint. This is the figure a dispute response cites.
      row('Card fingerprint', inst.fingerprint);
      row('Processor', inst.psp);
      row('Processor customer', inst.pspCustomerId);
      row('Processor payment method', inst.pspPaymentMethod);
      row('Saved on', dateTimeLong(inst.vaultedAt, (payload.contract || {}).timezone));
    }
    if (consent) {
      row('Authorised on', dateTimeLong(consent.acceptedAt, (payload.contract || {}).timezone));
      row('Authorisation IP', consent.ip);
      row('Device', consent.deviceId);
      row('CVC check', consent.cvcResult);
      row('Postcode check', consent.avsResult);
      row('Network transaction', consent.networkTxnId);
    }
  }

  function render(payload) {
    var c = payload.contract || {};
    var cur = c.currency;
    var plan = c.plan || {};
    var totals = c.totals || {};

    // ---- superseded banner. Must be obvious: a replaced agreement pretending to
    // be current is the one failure mode worse than not loading at all.
    var sup = root.querySelector('[data-superseded]');
    if (payload.superseded && sup) {
      sup.hidden = false;
      var detail = root.querySelector('[data-superseded-detail]');
      if (detail) {
        detail.textContent = ' Replaced on ' + (dateLong(payload.superseded.at) || '—')
          + (payload.superseded.reason ? ' (' + payload.superseded.reason + ').' : '.')
          + ' Ask us for the current version.';
      }
    }

    // ---- header
    set('orderReference', c.orderReference);
    set('shopifyOrderNumber', c.shopifyOrderNumber);
    var orderRow = root.querySelector('[data-shopify-order-row]');
    if (orderRow) orderRow.hidden = !c.shopifyOrderNumber;
    set('signedAt', dateLong((payload.signature && payload.signature.signedAt) || c.placedAt));

    // ---- parties
    var m = c.merchant || {};
    set('merchantName', m.name);
    lines(root.querySelector('[data-f="merchantAddress"]'), m.address);
    set('merchantEmail', m.contactEmail);

    var cust = c.customer || {};
    set('customerName', cust.name);
    lines(root.querySelector('[data-f="customerAddress"]'), cust.address);
    set('customerEmail', cust.email);
    set('customerPhone', cust.phone);

    // ---- summary. Every value straight from the snapshot's own plan block.
    // AU/NZ plans state installmentCents + totalPayments; US/UK plans state
    // instalmentCents + weeks. Read whichever the snapshot carries.
    set('cadence', cadenceLabel(plan));
    set('instalment', money(
      typeof plan.installmentCents === 'number' ? plan.installmentCents : plan.instalmentCents, cur));
    var count = typeof plan.totalPayments === 'number' ? plan.totalPayments : plan.weeks;
    set('weeks', typeof count === 'number'
      ? count + (plan.monthsApprox ? ' · about ' + plan.monthsApprox + ' months' : '')
      : null);
    // AU/NZ agreements take nothing at signing and their snapshots carry no
    // deposit figure — hide the tile rather than assert a number the document
    // does not contain.
    var depositStat = root.querySelector('[data-stat="deposit"]');
    if (typeof plan.depositCents === 'number') {
      set('deposit', money(plan.depositCents, cur));
    } else if (depositStat) {
      depositStat.hidden = true;
    }
    set('total', money(typeof plan.totalCents === 'number' ? plan.totalCents : totals.totalCents, cur));

    // ---- items
    var list = root.querySelector('[data-items]');
    if (list) {
      list.textContent = '';
      var items = c.items || [];
      for (var i = 0; i < items.length; i++) {
        var it = items[i];
        var li = document.createElement('li');
        li.className = 'contract-view__item';

        if (it.imageUrl) {
          var fig = document.createElement('div');
          fig.className = 'contract-view__item-media';
          var img = document.createElement('img');
          img.src = it.imageUrl;
          img.alt = '';
          img.loading = 'lazy';
          fig.appendChild(img);
          li.appendChild(fig);
        }

        var body = document.createElement('div');
        body.className = 'contract-view__item-body';

        var t = document.createElement('p');
        t.className = 'contract-view__item-title';
        // Frozen title — deliberately NOT a link to the live product, whose name
        // and price may since have changed. The contract records what was sold.
        t.textContent = it.title + (it.variantTitle ? ' — ' + it.variantTitle : '');
        body.appendChild(t);

        var meta = document.createElement('p');
        meta.className = 'contract-view__item-meta';
        var bits = [];
        if (it.sku) bits.push('SKU ' + it.sku);
        if (it.quantity) bits.push('Qty ' + it.quantity);
        if (typeof it.unitPriceCents === 'number') bits.push(money(it.unitPriceCents, cur) + ' each');
        meta.textContent = bits.join(' · ');
        body.appendChild(meta);

        li.appendChild(body);

        var amt = document.createElement('p');
        amt.className = 'contract-view__item-amount';
        amt.textContent = money(it.lineTotalCents, cur) || '—';
        li.appendChild(amt);

        list.appendChild(li);
      }
    }

    set('subtotal', money(totals.subtotalCents, cur));
    set('totalAgain', money(totals.totalCents, cur));

    var shipRow = root.querySelector('[data-row="shipping"]');
    if (shipRow && typeof totals.shippingCents === 'number' && totals.shippingCents > 0) {
      shipRow.hidden = false;
      set('shipping', money(totals.shippingCents, cur));
    }
    var taxRow = root.querySelector('[data-row="tax"]');
    // AU/NZ snapshots record GST included in the total (gstCents); US/UK record
    // added tax (taxCents). null means none was recorded — leave the row hidden.
    var taxCents = typeof totals.gstCents === 'number' ? totals.gstCents : totals.taxCents;
    if (taxRow && typeof taxCents === 'number') {
      taxRow.hidden = false;
      set('tax', money(taxCents, cur));
    }

    // ---- schedule
    var tbody = root.querySelector('[data-schedule]');
    var schedule = c.schedule || [];
    if (tbody) {
      tbody.textContent = '';
      var sum = 0;
      for (var s = 0; s < schedule.length; s++) {
        var p = schedule[s];
        sum += (typeof p.amountCents === 'number' ? p.amountCents : 0);
        var tr = document.createElement('tr');
        var th = document.createElement('th');
        th.setAttribute('scope', 'row');
        th.textContent = p.n;
        var td1 = document.createElement('td');
        td1.textContent = dateLong(p.dueDate) || p.dueDate || '—';
        var td2 = document.createElement('td');
        td2.className = 'ta-right';
        td2.textContent = money(p.amountCents, cur) || '—';
        tr.appendChild(th); tr.appendChild(td1); tr.appendChild(td2);
        tbody.appendChild(tr);
      }
      // Summed from the frozen rows, not recomputed from the plan — if the two
      // ever disagreed, showing the sum of what is listed is the honest number.
      set('scheduleTotal', money(sum, cur));
    }

    // Product swaps carry payments from the previous agreement. The headline
    // balance is the sum of the frozen remaining schedule, never a live balance
    // or a recalculation from today's product price.
    var transfer = c.transfer;
    var transferBlock = root.querySelector('[data-transfer]');
    if (transfer && typeof transfer.creditAppliedCents === 'number' && transferBlock) {
      transferBlock.hidden = false;
      set('transferSource', transfer.fromOrderReference || transfer.fromContractId);
      set('transferDate', dateLong(transfer.transferredAt));
      set('transferCredit', money(transfer.creditAppliedCents, cur));
      set('totalLabel', 'Still to pay');
      set('productTotalLabel', 'New agreement total');
      var transferCancellation = root.querySelector('[data-transfer-cancellation]');
      if (transferCancellation && typeof transfer.minCancellationFeeCents === 'number') {
        transferCancellation.hidden = false;
        set('transferFeeFloor', money(transfer.minCancellationFeeCents, cur));
      }
      var transferredStat = root.querySelector('[data-stat="transfer"]');
      if (transferredStat) transferredStat.hidden = false;
      var paidTodayStat = root.querySelector('[data-stat="deposit"]');
      if (paidTodayStat) paidTodayStat.hidden = true;
      var remaining = null;
      if (Array.isArray(c.schedule)) {
        remaining = 0;
        for (var t = 0; t < c.schedule.length; t++) {
          if (typeof c.schedule[t].amountCents !== 'number' || !isFinite(c.schedule[t].amountCents)) {
            remaining = null;
            break;
          }
          remaining += c.schedule[t].amountCents;
        }
      }
      set('total', money(remaining, cur));
      // An empty schedule alone is not evidence of full payment.
      if (remaining === 0 && transfer.creditAppliedCents === totals.totalCents) {
        var paidNotice = root.querySelector('[data-transfer-paid]');
        if (paidNotice) paidNotice.hidden = false;
        set('cadence', 'Payment status');
        set('instalment', 'Paid in full');
      }
    }

    // ---- prose blocks. textContent, not innerHTML: these are stored values.
    var fee = root.querySelector('[data-f="feePolicy"]');
    if (fee) fee.textContent = c.feePolicy || '';

    var terms = c.terms || payload.terms || {};
    set('termsVersion', terms.version);
    set('termsHash', terms.sha256 ? terms.sha256.slice(0, 16) : null);
    var tt = root.querySelector('[data-f="termsText"]');
    if (tt) {
      tt.textContent = '';
      var paras = String(terms.text || '').split(/\n{2,}/);
      for (var q = 0; q < paras.length; q++) {
        if (!paras[q].trim()) continue;
        var pEl = document.createElement('p');
        pEl.textContent = paras[q].trim();
        tt.appendChild(pEl);
      }
    }

    // ---- what the customer agreed to: the two consents the contract row records
    var agreements = (payload.evidence && payload.evidence.agreements) || null;
    var consentsBlock = root.querySelector('[data-consents-block]');
    var consentsList = root.querySelector('[data-consents]');
    if (agreements && consentsBlock && consentsList) {
      var agreed = [];
      if (agreements.terms) agreed.push('The Terms and Conditions, Privacy Policy and Cancellation Policy' + (terms.version ? ' (agreement version ' + terms.version + ')' : ''));
      if (agreements.cancellation) agreed.push('The cancellation terms');
      consentsList.innerHTML = '';
      agreed.forEach(function (text) { var li = document.createElement('li'); li.textContent = text; consentsList.appendChild(li); });
      consentsBlock.hidden = agreed.length === 0;
    }

    // ---- signature
    var sig = payload.signature || (c.signature || {});
    set('signatureName', sig.name);
    set('signedAtFull', dateTimeLong(sig.signedAt, c.timezone));
    var sigImg = root.querySelector('[data-sig-image]');
    var sigMissing = root.querySelector('[data-sig-missing]');
    if (sig.imageUrl && sigImg) {
      sigImg.src = sig.imageUrl;
      sigImg.hidden = false;
    } else if (sigMissing) {
      sigMissing.hidden = false;
    }

    renderEvidence(payload, cur);

    // ---- Direct Debit Authority download (NZ bank-rail only). The API sends
    // an absolute URL under this page's own token; no URL, no block.
    var ddaBlock = root.querySelector('[data-dda-block]');
    var dda = root.querySelector('[data-dd-authority]');
    if (ddaBlock && dda && payload.ddAuthorityUrl) {
      dda.href = payload.ddAuthorityUrl;
      ddaBlock.hidden = false;
    }

    set('issuedAt', dateLong(payload.issuedAt));

    show('doc');
  }

  function load() {
    var token = params.get('t');
    if (!token) { show('missing'); return; }

    show('loading');
    fetch(endpoint + '/api/public/contract/' + encodeURIComponent(token), {
      headers: { Accept: 'application/json' }
    })
      .then(function (res) {
        if (res.status === 404) { show('missing'); return null; }
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json();
      })
      .then(function (payload) {
        if (!payload) return;
        if (!payload.contract) { show('missing'); return; }
        render(payload);
      })
      .catch(function (err) {
        // Never fall back to live data here. A contract that cannot be loaded is
        // an error; a contract assembled from today's order would be a forgery.
        if (window.console) console.error('[contract-view]', err);
        show('error');
      });
  }

  var retry = root.querySelector('[data-retry]');
  if (retry) retry.addEventListener('click', load);

  var printBtn = root.querySelector('[data-print]');
  if (printBtn) printBtn.addEventListener('click', function () { window.print(); });

  // The schedule ships collapsed on screen, but the printed/PDF copy must be
  // the complete document — expand every <details> for print and restore the
  // reader's state afterwards. beforeprint also covers Cmd/Ctrl+P.
  var openedForPrint = [];
  window.addEventListener('beforeprint', function () {
    openedForPrint = [];
    var all = root.querySelectorAll('details[data-collapse]');
    for (var i = 0; i < all.length; i++) {
      if (!all[i].open) { all[i].open = true; openedForPrint.push(all[i]); }
    }
  });
  window.addEventListener('afterprint', function () {
    for (var i = 0; i < openedForPrint.length; i++) openedForPrint[i].open = false;
    openedForPrint = [];
  });

  load();
})();
